// Detects storage writes made by browser extensions so consent enforcement doesn't block them.

// Chromium family (Chrome, Chromium Edge, Brave, Opera) all use `chrome-extension://`.
// Firefox uses `moz-extension://`; Safari uses `safari-web-extension://`.
// `ms-browser-extension://` is the legacy EdgeHTML
export const EXT_SCHEMES: RegExp =
	/(chrome-extension|moz-extension|safari-web-extension|ms-browser-extension):\/\/([^/)\s]+)/;

// A frame's script location, minus the `:line:column`. Matches V8's `at fn (url:1:2)` and
// SpiderMonkey/JavaScriptCore's `fn@url:1:2`.
const FRAME_LOCATION = /([a-z][a-z0-9+.-]*:\/\/[^\s)]+?)(?::\d+){0,2}\)?$/i;

// A frame line, not the `Error: ` header V8 prefixes the trace with. V8 starts frames with `at `; the
// other engines use `name@url` and emit no header, so filtering keeps indices comparable.
const FRAME_LINE = /^at\s|@/;

// How many leading frames can hold the code that made the write. BSC sits 2 frames above the caller —
// `captureCallerSignals` and the override closure — and neither can be inlined away: the closure is the
// value installed on `setItem`/the cookie accessor, and a minifier won't inline a function containing
// try/catch. The capture is deliberately one function for that reason.
//
// Bounding the scan is the point of this module. Matching an extension URL anywhere in the stack also
// matches every write made by Atlassian code that an extension merely called into, which turns the
// extension allowance into a blanket consent bypass for our own writes.
const ORIGINATOR_FRAME_WINDOW = 3;

// Nothing past the window is read. V8 defaults to 10 and a product can lower it, so pin it.
const CAPTURED_FRAME_LIMIT = 6;

export interface ExtensionCaller {
	scheme: string;
	id: string;
}

export interface CallerSignals {
	stack: Error;
}

const parseFrames = (stack: string): string[] =>
	stack
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => FRAME_LINE.test(line));

const asExtensionCaller = (frame: string | undefined): ExtensionCaller | null => {
	const extension = frame?.match(FRAME_LOCATION)?.[1]?.match(EXT_SCHEMES);

	return extension ? { scheme: extension[1], id: extension[2] } : null;
};

// The extension that called the override, not any extension in the chain.
// Reading `.stack` formats every retained frame. Slow path only
export const getExtensionCaller = (captured: Error | undefined): ExtensionCaller | null => {
	const stack = captured?.stack;

	if (!stack) {
		return null;
	}

	for (const frame of parseFrames(stack).slice(0, ORIGINATOR_FRAME_WINDOW)) {
		const caller = asExtensionCaller(frame);

		if (caller) {
			return caller;
		}
	}

	return null;
};

// Must be called synchronously from the override, before the consent check — once that resolves the
// caller's frames have left the stack. V8 only formats the frames when `.stack` is read.
export const captureCallerSignals = (): CallerSignals => {
	try {
		const previousLimit = Error.stackTraceLimit;
		Error.stackTraceLimit = CAPTURED_FRAME_LIMIT;
		const stack = new Error();
		Error.stackTraceLimit = previousLimit;

		return { stack };
	} catch {
		return { stack: new Error() };
	}
};

export const attributeExtensionCaller = (
	signals: CallerSignals | undefined,
): ExtensionCaller | null => (signals ? getExtensionCaller(signals.stack) : null);
